import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import api from "../../../api/Api.js";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Swal from "sweetalert2";

const DurationManage = () => {

  // =========================================
  // STATE
  // =========================================

  const [durations, setDurations] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);

  const [perPage] = useState(10);

  // Currently selected/editing row ID
  const [editingId, setEditingId] = useState(null);

  // Edit Days
  const [editDays, setEditDays] = useState("");

  // Edit Nights
  const [editNights, setEditNights] = useState("");

  // =========================================
  // FETCH DATA
  // =========================================

  useEffect(() => {
    fetchDurationData();
  }, []);

  const fetchDurationData = async () => {

    try {

      const res = await api.get(
        "/adminHolidays/get-holidays-duration-data"
      );

      console.log(
        "DURATION DATA =>",
        res?.data
      );

      setDurations(
        res?.data?.data || []
      );

    } catch (error) {

      console.error(
        "Fetch Duration Error =>",
        error
      );

      toast.error(
        error?.response?.data?.message ||
        "Failed to load Duration data"
      );

    }

  };

  // =========================================
  // DELETE DURATION
  // =========================================

  const handleDelete = async (id) => {

    if (!id) {

      toast.error(
        "Invalid Duration ID"
      );

      return;
    }

    // Prevent delete if another row is editing
    if (
      editingId !== null &&
      editingId !== id
    ) {

      toast.error(
        "Please update or cancel the current editing row first"
      );

      return;
    }

    const confirm = await Swal.fire({

      title: "Delete Duration?",

      text:
        "Do you want to delete this Duration?",

      icon: "warning",

      showCancelButton: true,

      confirmButtonText: "Yes, Delete",

      cancelButtonText: "Cancel",

      confirmButtonColor: "#dc3545",

    });

    if (!confirm.isConfirmed) {
      return;
    }

    try {

      const res = await api.delete(
        `/adminHolidays/holidays-duration-delete/${id}`
      );

      console.log(
        "DELETE RESPONSE =>",
        res?.data
      );

      if (res?.data?.success) {

        await Swal.fire({

          title: "Deleted!",

          text:
            res?.data?.message ||
            "Duration deleted successfully",

          icon: "success",

          confirmButtonText: "OK",

        });

        // Clear edit mode
        setEditingId(null);
        setEditDays("");
        setEditNights("");

        fetchDurationData();

      } else {

        toast.error(
          res?.data?.message ||
          "Failed to delete Duration"
        );

      }

    } catch (error) {

      console.error(
        "Delete Duration Error =>",
        error
      );

      toast.error(
        error?.response?.data?.message ||
        "Failed to delete Duration"
      );

    }

  };

  // =========================================
  // STATUS CHANGE
  // =========================================

  const handleStatusChange = async (
    id,
    is_active
  ) => {

    if (!id) {

      toast.error(
        "Invalid Duration ID"
      );

      return;
    }

    // Prevent status change while another row
    // is being edited
    if (
      editingId !== null &&
      editingId !== id
    ) {

      toast.error(
        "Please update or cancel the current editing row first"
      );

      return;
    }

    try {

      const res = await api.put(
        "/adminHolidays/holidays-duration-status",
        {
          id,
          is_active: Number(is_active),
        }
      );

      console.log(
        "STATUS RESPONSE =>",
        res?.data
      );

      if (res?.data?.success) {

        toast.success(
          res?.data?.message ||
          "Status updated successfully"
        );

        fetchDurationData();

      } else {

        toast.error(
          res?.data?.message ||
          "Failed to update status"
        );

      }

    } catch (error) {

      console.error(
        "Status Update Error =>",
        error
      );

      toast.error(
        error?.response?.data?.message ||
        "Failed to update status"
      );

    }

  };

  // =========================================
  // START EDIT
  // =========================================

  const handleEdit = (duration) => {

    /*
      IMPORTANT:

      If another row is already being edited,
      don't allow this row to enter edit mode.
    */

    if (
      editingId !== null &&
      editingId !== duration.id
    ) {

      toast.error(
        "Please update or cancel the current editing row first"
      );

      return;
    }

    /*
      Set only selected row ID.
      Therefore only this row will become editable.
    */

    setEditingId(
      duration.id
    );

    setEditDays(
      duration.days ?? ""
    );

    setEditNights(
      duration.nights ?? ""
    );

  };

  // =========================================
  // CANCEL EDIT
  // =========================================

  const handleCancelEdit = () => {

    setEditingId(null);

    setEditDays("");

    setEditNights("");

  };

  // =========================================
  // UPDATE DURATION
  // =========================================

  const handleUpdate = async (id) => {

    // =========================================
    // VALIDATION
    // =========================================

    if (
      String(editDays).trim() === ""
    ) {

      toast.error(
        "Days is required"
      );

      return;
    }

    if (
      String(editNights).trim() === ""
    ) {

      toast.error(
        "Nights is required"
      );

      return;
    }

    if (
      isNaN(Number(editDays))
    ) {

      toast.error(
        "Days must be a valid number"
      );

      return;
    }

    if (
      isNaN(Number(editNights))
    ) {

      toast.error(
        "Nights must be a valid number"
      );

      return;
    }

    if (
      Number(editDays) <= 0
    ) {

      toast.error(
        "Days must be greater than 0"
      );

      return;
    }

    if (
      Number(editNights) < 0
    ) {

      toast.error(
        "Nights cannot be negative"
      );

      return;
    }

    // =========================================
    // CONFIRMATION
    // =========================================

    const confirm = await Swal.fire({

      title: "Update Duration?",

      text:
        "Do you want to update this Duration?",

      icon: "question",

      showCancelButton: true,

      confirmButtonText: "Yes, Update",

      cancelButtonText: "Cancel",

      confirmButtonColor: "#0d6efd",

    });

    if (!confirm.isConfirmed) {
      return;
    }

    // =========================================
    // API CALL
    // =========================================

    try {

      const payload = {

        id,

        days: Number(editDays),

        nights: Number(editNights),

      };

      console.log(
        "UPDATE DURATION PAYLOAD =>",
        payload
      );

      const res = await api.put(
        "/adminHolidays/holidays-duration-update",
        payload
      );

      console.log(
        "UPDATE DURATION RESPONSE =>",
        res?.data
      );

      // =========================================
      // SUCCESS
      // =========================================

      if (res?.data?.success) {

        await Swal.fire({

          title: "Updated!",

          text:
            res?.data?.message ||
            "Duration updated successfully",

          icon: "success",

          confirmButtonText: "OK",

        });

        // Clear edit mode
        setEditingId(null);

        setEditDays("");

        setEditNights("");

        // Refresh table
        fetchDurationData();

      } else {

        toast.error(
          res?.data?.message ||
          "Unable to update Duration"
        );

      }

    } catch (error) {

      console.error(
        "Update Duration Error =>",
        error
      );

      toast.error(
        error?.response?.data?.message ||
        error?.message ||
        "Server Error"
      );

    }

  };

  // =========================================
  // PAGINATION
  // =========================================

  const indexOfLast =
    currentPage * perPage;

  const indexOfFirst =
    indexOfLast - perPage;

  const currentItems =
    durations.slice(
      indexOfFirst,
      indexOfLast
    );

  const totalPages =
    Math.ceil(
      durations.length / perPage
    );

  // =========================================
  // PAGE CHANGE
  // =========================================

  const handlePageChange = (page) => {

    if (
      page < 1 ||
      page > totalPages
    ) {
      return;
    }

    // Cancel edit mode when page changes
    setEditingId(null);

    setEditDays("");

    setEditNights("");

    setCurrentPage(page);

  };

  // =========================================
  // JSX
  // =========================================

  return (

    <div className="container-fluid py-3 bg-white min-vh-100">

      {/* =====================================
          HEADER
      ====================================== */}

      <div className="card shadow-sm border-0 mb-4 bg-primary-subtle rounded-3">

        <div className="card-body d-flex justify-content-between align-items-center">

          <h4 className="fw-bold mb-0">
            Manage Duration
          </h4>

          <Link
            to="/holidays/duration/add-duration"
            className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2"
          >

            <ArrowLeft size={16} />

            Add Duration

          </Link>

        </div>

      </div>

      {/* =====================================
          TABLE CARD
      ====================================== */}

      <div className="card shadow-sm border-0">

        <div className="table-responsive">

          <table className="table table-hover table-sm align-middle mb-0">

            {/* =================================
                TABLE HEADER
            ================================== */}

            <thead className="bg-light">

              <tr>

                <th>
                  Sl
                </th>

                <th>
                  Duration
                </th>

                <th>
                  Status
                </th>

                <th>
                  Action
                </th>

              </tr>

            </thead>

            {/* =================================
                TABLE BODY
            ================================== */}

            <tbody>

              {currentItems.length === 0 ? (

                <tr>

                  <td
                    colSpan="4"
                    className="text-center py-4 text-muted"
                  >
                    No Duration Found
                  </td>

                </tr>

              ) : (

                currentItems.map(
                  (duration, index) => {

                    // =================================
                    // CHECK CURRENT ROW
                    // =================================

                    const isEditing =
                      editingId ===
                      duration.id;

                    /*
                      If any row is editing and this
                      is NOT that row, disable actions.
                    */

                    const isOtherRowEditing =
                      editingId !== null &&
                      editingId !==
                        duration.id;

                    return (

                      <tr
                        key={
                          duration.id
                        }
                        className={
                          isEditing
                            ? "table-primary"
                            : ""
                        }
                      >

                        {/* =================================
                            SL
                        ================================== */}

                        <td>

                          {indexOfFirst +
                            index +
                            1}

                        </td>

                        {/* =================================
                            DURATION
                        ================================== */}

                        <td>

                          {isEditing ? (

                            /*
                              ONLY SELECTED ROW
                              WILL SHOW INPUTS
                            */

                            <div className="d-flex align-items-center gap-2 flex-wrap">

                              {/* DAYS */}

                              <div
                                className="input-group input-group-sm"
                                style={{
                                  width: "145px"
                                }}
                              >

                                <span className="input-group-text">

                                  <i
                                    className="fa fa-sun-o"
                                    aria-hidden="true"
                                    style={{
                                      color: "gold"
                                    }}
                                  ></i>

                                </span>

                                <input
                                  type="number"
                                  min="1"
                                  className="form-control"
                                  value={
                                    editDays
                                  }
                                  onChange={(e) =>
                                    setEditDays(
                                      e.target.value
                                    )
                                  }
                                  placeholder="Days"
                                />

                                <span className="input-group-text">
                                  Day
                                </span>

                              </div>

                              <span>
                                -
                              </span>

                              {/* NIGHTS */}

                              <div
                                className="input-group input-group-sm"
                                style={{
                                  width: "155px"
                                }}
                              >

                                <span className="input-group-text">

                                  <i
                                    className="fa fa-moon-o"
                                    aria-hidden="true"
                                    style={{
                                      color: "#000",
                                    }}
                                  ></i>

                                </span>

                                <input
                                  type="number"
                                  min="0"
                                  className="form-control"
                                  value={
                                    editNights
                                  }
                                  onChange={(e) =>
                                    setEditNights(
                                      e.target.value
                                    )
                                  }
                                  placeholder="Nights"
                                />

                                <span className="input-group-text">
                                  Night
                                </span>

                              </div>

                            </div>

                          ) : (

                            /*
                              NORMAL DISPLAY
                            */

                            <>

                              <span>

                                <i
                                  className="fa fa-sun-o"
                                  aria-hidden="true"
                                  style={{
                                    color: "gold"
                                  }}
                                ></i>

                                &nbsp;

                                {duration.days}

                                &nbsp;

                                Day

                              </span>

                              {" - "}

                              <span>

                                <i
                                  className="fa fa-moon-o"
                                  aria-hidden="true"
                                  style={{
                                    color: "#000"
                                  }}
                                ></i>

                                &nbsp;

                                {duration.nights}

                                &nbsp;

                                Night

                              </span>

                            </>

                          )}

                        </td>

                        {/* =================================
                            STATUS
                        ================================== */}

                        <td>

                          <select
                            className={`form-select form-select-sm ${
                              Number(
                                duration.is_active
                              ) === 1
                                ? "text-success"
                                : "text-danger"
                            }`}
                            value={
                              duration.is_active
                            }
                            disabled={
                              isEditing ||
                              isOtherRowEditing
                            }
                            onChange={(e) =>
                              handleStatusChange(
                                duration.id,
                                e.target.value
                              )
                            }
                            style={{
                              maxWidth: "120px"
                            }}
                          >

                            <option value="1">
                              Active
                            </option>

                            <option value="0">
                              Inactive
                            </option>

                          </select>

                        </td>

                        {/* =================================
                            ACTION
                        ================================== */}

                        <td>

                          {isEditing ? (

                            /*
                              SELECTED ROW ONLY
                              UPDATE + CANCEL
                            */

                            <>

                              {/* UPDATE */}

                              <button
                                type="button"
                                className="btn btn-sm btn-success me-2"
                                onClick={() =>
                                  handleUpdate(
                                    duration.id
                                  )
                                }
                              >

                                <i className="fa fa-check"></i>

                                &nbsp;

                                Update

                              </button>

                              {/* CANCEL */}

                              <button
                                type="button"
                                className="btn btn-sm btn-secondary"
                                onClick={
                                  handleCancelEdit
                                }
                              >

                                <i className="fa fa-times"></i>

                                &nbsp;

                                Cancel

                              </button>

                            </>

                          ) : (

                            /*
                              NORMAL ROW
                            */

                            <>

                              {/* =================================
                                  EDIT BUTTON
                              ================================== */}

                              <button
                                type="button"
                                className="btn btn-sm btn-primary me-2"
                                disabled={
                                  isOtherRowEditing
                                }
                                onClick={() =>
                                  handleEdit(
                                    duration
                                  )
                                }
                                title={
                                  isOtherRowEditing
                                    ? "Please update or cancel current editing row first"
                                    : "Edit Duration"
                                }
                              >

                                <i className="fa fa-pencil"></i>

                              </button>

                              {/* =================================
                                  DELETE BUTTON
                              ================================== */}

                              <button
                                type="button"
                                className="btn btn-sm btn-danger"
                                disabled={
                                  isOtherRowEditing
                                }
                                onClick={() =>
                                  handleDelete(
                                    duration.id
                                  )
                                }
                                title={
                                  isOtherRowEditing
                                    ? "Please update or cancel current editing row first"
                                    : "Delete Duration"
                                }
                              >

                                <i className="fa fa-trash"></i>

                              </button>

                            </>

                          )}

                        </td>

                      </tr>

                    );

                  }
                )

              )}

            </tbody>

          </table>

        </div>

        {/* =====================================
            PAGINATION
        ====================================== */}

        {totalPages > 0 && (

          <div className="d-flex justify-content-center mt-3 mb-3">

            <ul className="pagination mb-0">

              {/* PREVIOUS */}

              <li
                className={`page-item ${
                  currentPage === 1
                    ? "disabled"
                    : ""
                }`}
              >

                <button
                  type="button"
                  className="page-link"
                  disabled={
                    currentPage === 1
                  }
                  onClick={() =>
                    handlePageChange(
                      currentPage - 1
                    )
                  }
                >
                  Prev
                </button>

              </li>

              {/* PAGE NUMBERS */}

              {Array.from(
                {
                  length: totalPages
                },
                (_, i) => (

                  <li
                    key={i}
                    className={`page-item ${
                      currentPage === i + 1
                        ? "active"
                        : ""
                    }`}
                  >

                    <button
                      type="button"
                      className="page-link"
                      onClick={() =>
                        handlePageChange(
                          i + 1
                        )
                      }
                    >
                      {i + 1}
                    </button>

                  </li>

                )
              )}

              {/* NEXT */}

              <li
                className={`page-item ${
                  currentPage === totalPages
                    ? "disabled"
                    : ""
                }`}
              >

                <button
                  type="button"
                  className="page-link"
                  disabled={
                    currentPage ===
                    totalPages
                  }
                  onClick={() =>
                    handlePageChange(
                      currentPage + 1
                    )
                  }
                >
                  Next
                </button>

              </li>

            </ul>

          </div>

        )}

      </div>

    </div>

  );

};

export default DurationManage;